import React, { useMemo } from 'react';

export default function AIAvatar() {
  const BACKEND = useMemo(() => {
    const env = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
    if (env) return env;
    if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
      // return 'http://localhost:8000';
      return 'https://mhub.maskantech.in';
    }
    return '';
  }, []);
  const DEMO_URL = `${BACKEND}/api/avatar/demo-video`;

  return (
    <div className="page">
      <header className="header"><h1>AI Avatar</h1></header>
      <main className="container">
        {/* Title + Intro (centered) */}
        <section className="panel avatar-hero">
          <h2 className="title-center">Voice-to-Avatar: Real Conversations, On Brand</h2>
          <p className="lead-center">
            Our AI Avatar takes user speech, transcribes it (STT), reasons with an LLM, synthesizes a natural voice (TTS),
            and renders a lifelike avatar video - all in one seamless pipeline.
          </p>
        </section>

        {/* How it works */}
        <section className="panel avatar-flow">
          <h3 className="flow-title">How It Works</h3>
          <div className="flow-grid">
            <div className="flow-step">
              <div className="ico">🎤</div>
              <div className="step-title">1) Capture</div>
              <p>Record the user's microphone audio securely.</p>
              <div className="mini">Streaming WebAudio / WebRTC capture</div>
            </div>
            <div className="flow-step">
              <div className="ico">📝</div>
              <div className="step-title">2) STT</div>
              <p>Accurate speech-to-text transcription in real time.</p>
              <div className="mini">Punctuation, diarization, timestamps</div>
            </div>
            <div className="flow-step">
              <div className="ico">🧠</div>
              <div className="step-title">3) LLM</div>
              <p>Reasoning over context, tools, and policy with persona.</p>
              <div className="mini">OpenRouter LLMs; tool and memory hooks</div>
            </div>
            <div className="flow-step">
              <div className="ico">🔊</div>
              <div className="step-title">4) TTS</div>
              <p>Neural voice synthesis with prosody and style control.</p>
              <div className="mini">SSML / style tokens, multilingual</div>
            </div>
            <div className="flow-step">
              <div className="ico">👤</div>
              <div className="step-title">5) Avatar</div>
              <p>Audio-driven animation renders a realistic avatar video.</p>
              <div className="mini">Lip-sync, eye gaze, expression blend</div>
            </div>
            <div className="flow-step">
              <div className="ico">📤</div>
              <div className="step-title">6) Delivery</div>
              <p>Stream to the browser and provide MP4 for download.</p>
              <div className="mini">H.264 MP4, yuv420p, faststart</div>
            </div>
          </div>
          <div className="notes-band">
            <div className="notes-title">Key Advantages</div>
            <div className="notes-grid">
              <div className="note-item"><span className="ico">⚡</span><span>Natural turn-taking with low latency</span></div>
              <div className="note-item"><span className="ico">🎭</span><span>Custom voices and brand personas</span></div>
              <div className="note-item"><span className="ico">📤</span><span>Exportable, sharable video responses</span></div>
            </div>
          </div>
        </section>

        {/* Optional quick stats */}
        <section className="panel avatar-details">
          <div className="cards">
            <div className="card">
              <div className="cap">Latency</div>
              <div className="big">~1-3s</div>
              <div className="muted">Depends on model + network; streaming reduces perceived delay.</div>
            </div>
            <div className="card">
              <div className="cap">Quality</div>
              <div className="big">HD Video</div>
              <div className="muted">H.264 profile for wide browser compatibility.</div>
            </div>
            <div className="card">
              <div className="cap">Brand</div>
              <div className="big">Custom Persona</div>
              <div className="muted">Scriptable voice, style, and knowledge grounding.</div>
            </div>
            <div className="card">
              <div className="cap">Privacy</div>
              <div className="big">Configurable</div>
              <div className="muted">PII redaction, secure transport, and scoped retention.</div>
            </div>
          </div>
        </section>

        {/* Full-width demo video */}
        <section className="panel avatar-demo">
          <div className="video-full">
            <video src={DEMO_URL} controls preload="metadata" playsInline style={{ width: '100%', height: '100%', objectFit: 'contain' }}>
              Your browser does not support the video tag.
            </video>
          </div>
        </section>

        {/* CTA below video */}
        <section className="panel avatar-cta">
          <div className="cta-body">
            <div>
              <h3>See It In Action</h3>
              <p>Play the demo video above. Want a live capture with your voice and persona? We can wire mic input and render on demand.</p>
            </div>
            <div className="cta-actions">
              <a className="btn-primary" href={DEMO_URL} download>
                Download Demo MP4
              </a>
            </div>
          </div>
        </section>

        <style>{`
          .avatar-hero { background: linear-gradient(180deg,#f7fbff,#ecf5ff); border:1px solid #e6eaf1; text-align:center; }
          .title-center { margin: 0 0 8px; font-size: 28px; font-weight: 800; }
          .lead-center { color:#2a3553; max-width: 900px; margin: 0 auto; }

          .avatar-flow .flow-title { margin:0 0 12px; font-size: 20px; }
          .flow-grid { display:grid; grid-template-columns: repeat(3, 1fr); gap:12px; }
          .flow-step { background:#fff; border:1px solid #e6eaf1; border-radius:12px; padding:14px; box-shadow:0 8px 24px rgba(2,6,23,.06); }
          .flow-step .ico { width:36px; height:36px; display:flex; align-items:center; justify-content:center; background:#eef3ff; border:1px solid #d7e1ff; border-radius:10px; margin-bottom:8px; }
          .step-title { font-weight:700; margin-bottom:4px; }
          .mini { color:#6b7a99; font-size:12px; margin-top:6px; }
          .notes-band { margin-top:12px; background:#eaf1ff; border:1px solid #cdd8ff; border-radius:14px; padding:16px; text-align:center; }
          .notes-title { color:#2C388F; font-weight:800; font-size:18px; }
          .notes-grid { margin-top:10px; display:grid; grid-template-columns: repeat(3, 1fr); gap:12px; color:#2C388F; font-weight:600; }
          .note-item { display:flex; align-items:center; justify-content:center; gap:8px; text-align:center; }
          .note-item .ico { width:28px; height:28px; display:inline-flex; align-items:center; justify-content:center; background:#dfe8ff; border:1px solid #cdd8ff; border-radius:8px; }

          .avatar-details .cards { display:grid; grid-template-columns: repeat(4, 1fr); gap:12px; }
          .avatar-details .card { background:#eaf1ff; border:1px solid #cdd8ff; color:#2C388F; border-radius:14px; padding:16px; box-shadow:0 8px 18px rgba(2,6,23,.06); }
          .avatar-details .cap { color:#2C388F; opacity:.9; text-transform: uppercase; letter-spacing:.06em; font-size:12px; }
          .avatar-details .big { color:#2C388F; font-size:22px; font-weight:800; margin:6px 0; }

          .avatar-demo .video-full { width:100%; height: 78vh; background:#000; border-radius:12px; overflow:hidden; border:1px solid #cfd9ea; }

          .avatar-cta .cta-body { display:flex; align-items:center; justify-content:space-between; gap:12px; }
          .avatar-cta .cta-actions { display:flex; align-items:center; }
          .avatar-cta .btn-primary { background:#1e2a55; border:1px solid #1e2a55; color:#fff; padding:12px 16px; border-radius:12px; text-decoration:none; display:inline-flex; align-items:center; white-space:nowrap; }

          @media (max-width: 960px) {
            .flow-grid { grid-template-columns: 1fr; }
            .avatar-details .cards { grid-template-columns: 1fr 1fr; }
            .avatar-demo .video-full { height: 50vh; }
            .notes-grid { grid-template-columns: 1fr; }
          }
        `}</style>
      </main>
    </div>
  );
}
